import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ReportsDetailHeader, { ReportHeaderData } from "./component/ReportsDetailHeader";
import ReportCategoryAccess, { AccessInfo } from "./component/ReportCategoryAccess";
import { axiosRestInstance } from "../../../../../common/axios";
import Graph from "./component/Graph";
import DataTable from "./component/DataTable";
import FilterPanel from "./component/filter-panel/FilterPanel";
import { COUNT_AXIS_OPTION } from "./component/filter-panel/GraphTab";
import ReportEmpty from "../report-empty";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { columnFilterLabelType, postJoinedTablesFieldsDataAPI, postJoinedTablesFieldsDataType, SaveReportPayload } from "../../../../../redux/saga/partner-details";
import { partnerDetailsSliceAction } from "../../../../../redux/slices/partner-details";
import { exportReportSagaAction, saveReportDataSagaAction } from "../../../../../redux/saga/partner-details/saga-actions";
import ErrorPopup from "../error-popup";
import SaveAsReportModal from "../save-as-report-modal";
import { useSessionCanEditModule } from "../../../../../hooks/useSessionPermissions";
import { REPORTS_DASHBOARD_PERMISSION_KEYS as RD_KEYS } from "../../../../../utils/permissions/reportsDashboardPermissionKeys";
import { canEditCategoryByRole } from "../../../../../utils/permissions/categoryAccess";

/* ---------- Types ---------- */
type CategoryState = {
  key: string;
  title: string;
  description: string
};

type TableColumn = {
  value: string;
  label: string;
  dataKey: string;
};

const resolveDataKey = (value: string, alias?: string) =>
  (alias && alias.trim()) || value.split(".").pop() || value;

export type ReportFilter = {
  field: string;
  operator: "=" | "!=" | ">" | "<" | "IN" | "NOT IN";
  value: string | string[];
};

type ChartSettings = {
  applied: boolean;
  chartType: "line" | "bar";
  title: string;
  xAxis: string;
  yAxis: string;
  field1: string;
  field2: string;
  year: string;
  dateRange: {
    from: string | null;
    to: string | null;
  };
};

type SortRule = {
  column: string;
  direction: "asc" | "desc";
};

type resetFilterState = {
  title: string;
  selectedColumns: string[];
  groupColumns: string[];
  groupRows: string[];
}

const ReportsDetailPage: React.FC = () => {
  const isHeaderInitialized = useRef(false);

  const { categoryKey, reportId } = useParams<{
    categoryKey: string;
    reportId?: string;
  }>();

  const location = useLocation();
  const navigate = useNavigate();
  //const categoryFromState = location.state?.category as CategoryState | undefined;

  const [headerData, setHeaderData] = useState<ReportHeaderData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [accessInfo, setAccessInfo] = useState<AccessInfo>({ viewOnly: [], edit: [] });

  const [columnSorts, setColumnSorts] = useState<SortRule[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<ReportFilter[]>([]);
  const [canExport, setCanExport] = useState(false);
  const [reportMode, setReportMode] = useState<"LIVE" | "SNAPSHOT">("LIVE");
  const saveAttemptRef = useRef(false);
  const pendingNavigateReportId = useRef<number | null>(null);

  const [resetState, setResetState] = useState<resetFilterState>({
    title: "Untitled",
    selectedColumns: [],
    groupColumns: [],
    groupRows: [],
  });

  const [popup, setPopup] = useState({
    open: false,
    message: "",
    type: "error" as "success" | "error",
  });
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsLoading, setSaveAsLoading] = useState(false);

  const [chartSettings, setChartSettings] = useState<ChartSettings>({
    applied: false,
    chartType: "line",
    title: "",
    xAxis: "",
    yAxis: "",
    field1: "",
    field2: "",
    year: "",
    dateRange: { from: null, to: null },
  });

  const dispatch = useAppDispatch();

  const [tableData, setTableData] = useState<any[]>([]);

  const { reportFilter, showTableData }: { reportFilter: postJoinedTablesFieldsDataType, showTableData: any } = useAppSelector((state) => state.partner_details);
  const { chart }: { chart: any } = useAppSelector((state) => state.partner_details);
  //const chart = reportFilter.chart;
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const categoryManagementEdit = useSessionCanEditModule(
    RD_KEYS.CATEGORY_MANAGEMENT
  );
  const {
    report_id,
    status: { updatePartnerMessage, updatePartnerStatus, exportReportLoading },
  } = useAppSelector((state) => state.partner_details);

  const isEditMode = Boolean(reportId);

  const categoryLevelEdit = canEditCategoryByRole(
    loggedUserDetail?.role,
    accessInfo.edit
  );
  const canEditReport = categoryManagementEdit && categoryLevelEdit;
  const isSnapshotReport = reportMode === "SNAPSHOT";
  /** LIVE: category edit permission. SNAPSHOT: export-only for everyone (including super admin). */
  const canSaveAndApply = canEditReport && !isSnapshotReport;

  useEffect(() => {
    if (!isEditMode && headerData?.title) {
      dispatch(
        partnerDetailsSliceAction.setReportFilterTitleAction(headerData.title)
      );
    }
  }, [headerData?.title, dispatch, isEditMode]);

  const exportDisabled =
    (!canExport && !isEditMode && !isSnapshotReport) || !!exportReportLoading;
  const mapOperatorToBackend = (
    uiOp: "=" | "!=" | ">" | "<" | "IN" | "NOT IN"
  ) => {
    return uiOp;
  };

  const buildFiltersPayload = (filters: ReportFilter[]) =>
    filters.map((filter) => ({
      field: filter.field,
      operator: mapOperatorToBackend(filter.operator),
      value: filter.value,
    }));

  const getValidFilters = (
    selectedColumns: string[],
    filters: ReportFilter[]
  ) => filters.filter((filter) => selectedColumns.includes(filter.field));

  useEffect(() => {
    if (saveAttemptRef.current && report_id && report_id > 0) {
      setPopup({
        open: true,
        message: updatePartnerMessage || "Report saved successfully.",
        type: "success",
      });
    }
  }, [report_id, updatePartnerMessage]);

  useEffect(() => {
    if (saveAttemptRef.current && !report_id && updatePartnerMessage && updatePartnerStatus === false) {
      setPopup({
        open: true,
        message: updatePartnerMessage,
        type: "error",
      });
    }
  }, [updatePartnerMessage, updatePartnerStatus, report_id]);

  const closePopup = () => {
    setPopup({ open: false, message: "", type: "error" });
    dispatch(partnerDetailsSliceAction.resetSaveReportStatus());

    // navigate AFTER user closes success popup
    if (pendingNavigateReportId.current) {
      navigate(
        `/hid-admin/dashboard/reports-dashboard/category/${categoryKey}/report/${pendingNavigateReportId.current}`,
        { replace: true }
      );
      pendingNavigateReportId.current = null;
    }
  };

  // category listing in dropdown
  const getCategories = async () => {
    try {
      const response = await axiosRestInstance.get("/category/all");
      if (response.data?.success) {
        const categories = response.data.data.map((c: any) => ({
          key: c.ID.toString(),
          title: c.name,
          description: c.description,
          user_view: c.user_view || [],
          user_edit: c.user_edit || [],
        }));
        setCategoriesList(categories);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    if (!categoryKey) {
      navigate("/hid-admin/dashboard/reports-dashboard");
      return;
    }

    if (categoriesList.length === 0) return;
    if (isHeaderInitialized.current) return;

    const selectedCategoryData = categoriesList.find(
      c => c.key === categoryKey
    );

    if (!selectedCategoryData) {
      navigate("/hid-admin/dashboard/reports-dashboard");
      return;
    }

    setHeaderData(prev => ({
      id: reportId ? reportId : undefined, 
      title: prev?.title || "Untitled",
      categoryKey: selectedCategoryData.key,
      categoryTitle: selectedCategoryData.title,
      isNew: !reportId,
    }));

    // ONLY category title goes to dropdown
    setSelectedCategory(selectedCategoryData.title);

    setAccessInfo({
      viewOnly: selectedCategoryData.user_view,
      edit: selectedCategoryData.user_edit,
    });

    isHeaderInitialized.current = true;
  }, [categoryKey, categoriesList, navigate, reportId]);

  const handleCategoryChange = (categoryTitle: string) => {
    const selected = categoriesList.find(c => c.title === categoryTitle);
    if (!selected) return;

    //setSelectedCategory(selected.title);
    dispatch(partnerDetailsSliceAction.setReportFilterChartAction({}));
    dispatch(partnerDetailsSliceAction.setChartDataAction(null));

    navigate(
      `/hid-admin/dashboard/reports-dashboard/category/${selected.key}?mode=add`,
      { replace: true }
    );

    setAccessInfo({
      viewOnly: selected.user_view,
      edit: selected.user_edit,
    });
  };

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [groupColumns, setGroupColumns] = useState<string[]>([]);
  const [groupRows, setGroupRows] = useState<string[]>([]);
  const [showTable, setShowTable] = useState(false);
  const [appliedColumns, setAppliedColumns] = useState<string[]>([]);

  const runReportAPI = async (userId: number, reportId: string) => {
    const res = await axiosRestInstance.get(
      `/report/run/?user_id=${userId}&report_id=${reportId}`
    );
    return res.data;
  };

  //const isEditMode = Boolean(reportId);

  useEffect(() => {
    if (!reportId) {
      setReportMode("LIVE");
      return;
    }

    setReportMode("LIVE");

    const loadReport = async () => {
      try {
        const userId = loggedUserDetail.ID;
        const res = await runReportAPI(userId, reportId);
        
        if (!res?.success) return;

        const isSnapshot = res.report_mode === "SNAPSHOT";
        setReportMode(isSnapshot ? "SNAPSHOT" : "LIVE");

        const { report, snapshot } = res.data;

          dispatch(partnerDetailsSliceAction.setReportFilterTitleAction(report.title || "Untitled"))
          dispatch(partnerDetailsSliceAction.setReportFilterColumnAction(report.definition?.selectColumns || []))
          dispatch(partnerDetailsSliceAction.setReportFilterAction(report.definition?.filters || []))
          dispatch(partnerDetailsSliceAction.setReportFilterChartAction(report.definition.chart || {}))
          dispatch(partnerDetailsSliceAction.setChartDataAction(report.definition.chart || {}))
        
        // ----- Header (Report Title & Category) -----
        setHeaderData(prev => ({
          id: report.id.toString(),
          title: report.title || prev?.title || "Untitled",
          categoryKey: report.category_id.toString(),
          categoryTitle:
            categoriesList.find(c => c.key === report.category_id.toString())?.title || "",
          isNew: false,
        }));

        // ----- Outline Tab (Columns) -----
        const selectedCols = report.definition?.selectColumns || [];
        setAppliedColumns(selectedCols);             // what columns are selected in Outline
        setSelectedColumns(selectedCols);

        // ----- Filter Tab -----
        const savedFilters = Array.isArray(report.definition?.filters)
          ? report.definition.filters
          : report.definition?.filters
            ? [report.definition.filters]
            : [];
        setAppliedFilters(savedFilters);

        // ----- Chart Tab -----
        if (report.definition?.chart?.type) {
          const chartDef = report.definition.chart;
          setChartSettings({
            applied: true,
            chartType: chartDef.type,
            title: chartDef.title || "",
            xAxis: chartDef.xAxis || "",
            yAxis: chartDef.yAxis || "", // adjust if needed
            field1: chartDef.aggregation?.field1 || "",
            field2: chartDef.aggregation?.field2 || "",
            year: chartDef.dateField || "",
            dateRange: chartDef.dateRange || { from: null, to: null },
          });

          // Set chart data for Graph component
          dispatch(partnerDetailsSliceAction.setChartDataAction(snapshot.chart));
        }

        // ----- Table Data -----
        if (snapshot?.table) {
          setTableData(snapshot.table);
          setShowTable(snapshot.table.length > 0);
          dispatch(partnerDetailsSliceAction.setShowTableDataAction(snapshot.table));
        }

        if (isSnapshot && snapshot?.table?.length) {
          setCanExport(true);
        }
      } catch (err) {
        console.error("Failed to load report", err);
      }
    };

    loadReport();
  }, [reportId, categoriesList]);

  const normalizeChart = (chart: any): {
    type: "line" | "bar";
    categories: string[];
    series: { name: string; values: number[] }[];
  } => {
    if (!chart || !Array.isArray(chart.xAxis) || !Array.isArray(chart.series)) {
      return { type: "line", categories: [], series: [] };
    }

    return {
      type: chart.type,
      categories: chart.xAxis.map((x: any) =>
        x?.label !== undefined ? String(x.label) : ""
      ),
      series: chart.series.map((s: any) => ({
        name: s.name ?? "Value",
        values: Array.isArray(s.data) ? s.data : [],
      })),
    };
  };

  const { joinedTablesFields } = useAppSelector(
    (state) => state.partner_details
  );

  const tableColumns: TableColumn[] = useMemo(() => {
    return appliedColumns.map(col => {
      const match = joinedTablesFields?.find(
        (f: columnFilterLabelType) => f.value === col
      );

      return {
        value: col,
        label: match?.label || col,
        dataKey: resolveDataKey(col, match?.alias),
      };
    });
  }, [appliedColumns, joinedTablesFields]);

  const filteredRows = useMemo(() => {
    if (!showTable || appliedColumns.length === 0) return [];
    let rows = [...tableData];
    return rows;
  }, [showTable, appliedColumns, tableData, appliedFilters, columnSorts]);  // Recalculate filteredRows when these values change

  const hasTableData = showTable && filteredRows.length > 0;

  const columnLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    joinedTablesFields?.forEach((col: any) => {
      map[col.value] = col.label;
    });
    return map;
  }, [joinedTablesFields]);

  const chartData = useMemo(() => {
    if (!chart) {
      return { Data: [], AreaConfig: [], BarConfig: [], yAxisLabel: "", xAxisLabel: "" };
    }

    const normalized = normalizeChart(chart);

    const getAxisLabel = (value: string) =>
      value === COUNT_AXIS_OPTION.value
        ? COUNT_AXIS_OPTION.label
        : columnLabelMap[value] || value;

    // Determine X-axis label based on time granularity
    const xAxisLabel =
      chartSettings.year === "year"
        ? "Year"
        : chartSettings.year === "month"
          ? "Month"
          : getAxisLabel(chartSettings.xAxis);
    const yAxisLabel = getAxisLabel(chartSettings.yAxis);

    if (normalized.type === "line") {
      const Data = normalized.categories.map((label, index) => {
        const row: any = { key: label };
        normalized.series.forEach((s) => {
          row[s.name] = s.values[index] ?? 0;
        });
        return row;
      });

      const AreaConfig = normalized.series.map((s) => ({
        dataKey: s.name,
        label: s.name,
        fill: "#B3DDCC",
      }));

      return {
        Data,
        AreaConfig,
        xAxisLabel,
        yAxisLabel,
      };
    }

    if (normalized.type === "bar") {
      const colors = [
        "#00539B", "#F8971D", "#60116A", "#00AEEF",
        "#FFD200", "#B0CBEA", "#6A1B9A", "#2E7D32"
      ];

      const Data = normalized.categories.map((label, index) => {
        const row: any = { name: label };
        normalized.series.forEach((s) => {
          row[s.name] = s.values[index] ?? 0;
        });
        return row;
      });

      const BarConfig = normalized.series.map((s, index) => ({
        dataKey: s.name,
        label: s.name,
        fill: colors[index % colors.length],
      }));

      return {
        Data,
        BarConfig,
        xAxisLabel,
        yAxisLabel,
      };
    }

    return { Data: [], AreaConfig: [], BarConfig: [], xAxisLabel: "", yAxisLabel: "" };
  }, [chart, chartSettings.xAxis, chartSettings.yAxis, chartSettings.year, columnLabelMap]);

  const [loading, setLoading] = useState(false);

  const runReportWithFilters = async () => {
    if (isSnapshotReport) return;
    if (!headerData?.title || appliedColumns.length === 0) return;

    const filters = buildFiltersPayload(appliedFilters);

    const selectedDateField =
      chartSettings.year === "month"
        ? "month"
        : chartSettings.year === "year"
          ? "year"
          : "";

          console.log("Save Payload Chart Settings", chartSettings);

    const chart =
      chartSettings.applied
        ? {
            type: chartSettings.chartType,
            title: chartSettings.title || "",
            xAxis: chartSettings.xAxis || "",
            yAxis: chartSettings.yAxis || "",
            dateField: selectedDateField,
            dateRange: {
              from: chartSettings.dateRange.from || "",
              to: chartSettings.dateRange.to || "",
            },
            ...(chartSettings.yAxis === "AVG" && {
        aggregation: {
          field1: chartSettings.field1,
          field2: chartSettings.field2,
        },
      }),
          }
        : null;

    const payload = {
      report_title: headerData.title,
      selectColumns: appliedColumns,
      filters,
      chart, // ✅ REQUIRED
    };

    const response = await postJoinedTablesFieldsDataAPI(payload);

    if (response?.success) {
      // TABLE
      if (response.data?.table) {
        setTableData(response.data.table);
        dispatch(
          partnerDetailsSliceAction.setShowTableDataAction(response.data.table)
        );
      }

      // CHART
      if (response.data?.chart) {
        dispatch(
          partnerDetailsSliceAction.setChartDataAction(response.data.chart)
        );

        setChartSettings(prev => ({
          ...prev,
          applied: true,
        }));
      }
    }
  };
  
  const shouldResetChart = (
    selectedColumns: string[],
    chartSettings: ChartSettings
  ) => {
    if (!chartSettings.applied) return false;

    const { xAxis, yAxis } = chartSettings;
    const isMissingAxis = (axis: string) =>
      !!axis && !selectedColumns.includes(axis);

    // If either axis is missing in selected columns → reset chart
    if (
      isMissingAxis(xAxis) ||
      (yAxis !== COUNT_AXIS_OPTION.value && isMissingAxis(yAxis))
    ) {
      return true;
    }

    return false;
  };

  const handleApplyOutline = async ({ title, selectedColumns }: any) => {
    if (isSnapshotReport) return;

    // UPDATE HEADER TITLE
    setHeaderData(prev =>
      prev ? { ...prev, title } : prev
    );

    // Keep Redux in sync
    dispatch(
      partnerDetailsSliceAction.setReportFilterTitleAction(title)
    );

    setAppliedColumns(selectedColumns);

    // ---- VALIDATE FILTER ----
    const validFilters = getValidFilters(selectedColumns, appliedFilters);

    if (validFilters.length !== appliedFilters.length) {
      setAppliedFilters(validFilters);
      dispatch(partnerDetailsSliceAction.setReportFilterAction(validFilters));
    }

    // ---- VALIDATE CHART ----
    const resetChart = shouldResetChart(selectedColumns, chartSettings);

    let updatedChartSettings = chartSettings;

    if (resetChart) {
      updatedChartSettings = {
        ...chartSettings,
        applied: false,
        xAxis: "",
        yAxis: "",
        year: "",
        dateRange: { from: null, to: null },
      };

      setChartSettings(updatedChartSettings);
      dispatch(partnerDetailsSliceAction.setChartDataAction(null));
      dispatch(partnerDetailsSliceAction.setReportFilterChartAction({}));
    }

    // ---- BUILD PAYLOAD ----
    const filters = buildFiltersPayload(validFilters);

    const chart =
      !resetChart && updatedChartSettings.applied
        ? {
            type: updatedChartSettings.chartType,
            title: updatedChartSettings.title || "",
            xAxis: updatedChartSettings.xAxis || "",
            yAxis: updatedChartSettings.yAxis || "",
            dateField:
              updatedChartSettings.year === "year"
                ? "year"
                : updatedChartSettings.year === "month"
                ? "month"
                : "",
            dateRange: {
              from: updatedChartSettings.dateRange.from || "",
              to: updatedChartSettings.dateRange.to || "",
            },
          }
        : null;

    const payload = {
      report_title: title,
      selectColumns: selectedColumns,
      filters,
      chart,
    };

    const response = await postJoinedTablesFieldsDataAPI(payload);

    if (response?.success) {
      if (response.data?.table) {
        setTableData(response.data.table);
        dispatch(
          partnerDetailsSliceAction.setShowTableDataAction(response.data.table)
        );
      }

      if (response.data?.chart && !resetChart) {
        dispatch(
          partnerDetailsSliceAction.setChartDataAction(response.data.chart)
        );
      }
    }

    setShowTable(selectedColumns.length > 0);
  };

  useEffect(() => {
    // Run only when filters change AND columns already applied (skip for snapshot reports)
    if (!appliedColumns.length || isSnapshotReport) return;

    runReportWithFilters();
  }, [appliedFilters, isSnapshotReport]);

  const handleResetOutline = () => {
    setHeaderData(prev =>
      prev ? { ...prev, title: resetState.title || "Untitled" } : prev
    );
    setAppliedColumns(resetState.selectedColumns || []);
    setGroupColumns(resetState.groupColumns || []);
    setGroupRows(resetState.groupRows || [])
    setShowTable((resetState.selectedColumns || []).length > 0);
  };

  if (!headerData) return null;

  const validateSavePrerequisites = (): number | null => {
    const category_id = Number(headerData?.categoryKey ?? categoryKey);

    if (!Number.isFinite(category_id) || category_id <= 0) {
      setPopup({
        open: true,
        message: "Invalid category. Please select a valid category and try again.",
        type: "error",
      });
      return null;
    }

    const selectColumns = appliedColumns?.length ? appliedColumns : [];
    if (selectColumns.length === 0) {
      setPopup({
        open: true,
        message: "No columns selected. Select at least one column before saving.",
        type: "error",
      });
      return null;
    }

    return category_id;
  };

  const buildReportSavePayload = ({
    reportTitle,
    reportId,
    reportMode,
  }: {
    reportTitle: string;
    reportId?: number;
    reportMode?: "LIVE" | "SNAPSHOT";
  }): SaveReportPayload => {
    const category_id = Number(headerData?.categoryKey ?? categoryKey);
    const selectColumns = appliedColumns?.length ? appliedColumns : [];
    const filters = buildFiltersPayload(appliedFilters);

    const selectedDateField =
      chartSettings.year === "month"
        ? "month"
        : chartSettings.year === "year"
          ? "year"
          : "";

    const chart = {
  type: chartSettings.chartType || "",
  title: chartSettings.title || "",
  xAxis: chartSettings.xAxis || "",
  yAxis: chartSettings.yAxis || "",
  dateField: selectedDateField,
  dateRange: {
    from: chartSettings.dateRange.from || "",
    to: chartSettings.dateRange.to || "",
  },
  ...(chartSettings.yAxis === "AVG" && {
    aggregation: {
      field1: chartSettings.field1,
      field2: chartSettings.field2,
    },
  }),
};

    const payload: SaveReportPayload = {
      report_title: reportTitle,
      user: loggedUserDetail.ID,
      category_id,
      definition: {
        selectColumns,
        filters,
        chart,
      },
    };

    if (reportId != null) {
      payload.report_id = reportId;
    }

    if (reportMode) {
      payload.report_mode = reportMode;
    }

    return payload;
  };

  const dispatchSaveReport = (
    payload: SaveReportPayload,
    options?: {
      onSuccess?: (resp: {
        report_id: number;
        mode?: "created" | "updated" | "copy_created";
      }) => void;
    }
  ) => {
    dispatch(
      saveReportDataSagaAction({
        payload,
        onSuccess: (resp) => {
          options?.onSuccess?.(resp);

          const message =
            resp.mode === "updated"
              ? "Report updated successfully."
              : resp.mode === "copy_created"
                ? "Report copy saved successfully."
                : "Report saved successfully.";

          setPopup({
            open: true,
            message,
            type: "success",
          });

          setCanExport(true);
        },
        onError: (errorMessage: string) => {
          setPopup({
            open: true,
            message: errorMessage || "Unable to save report.",
            type: "error",
          });
          setCanExport(false);
        },
      })
    );
  };

  const onSaveReport = () => {
    if (!canSaveAndApply) return;
    saveAttemptRef.current = true;
    if (validateSavePrerequisites() == null) return;

    const report_title = (headerData?.title || "Untitled").trim();
    const payload = buildReportSavePayload({
      reportTitle: report_title,
      reportId: headerData.id ? Number(headerData.id) : undefined,
      reportMode: isSnapshotReport ? "SNAPSHOT" : "LIVE",
    });

    dispatchSaveReport(payload, {
      onSuccess: (resp) => {
        if (!headerData?.id && resp?.report_id) {
          setHeaderData((prev) =>
            prev
              ? {
                  ...prev,
                  id: String(resp.report_id),
                  isNew: false,
                }
              : prev
          );
          pendingNavigateReportId.current = resp.report_id;
        }
      },
    });
  };

  const onOpenSaveAs = () => {
    if (!canSaveAndApply) return;
    if (validateSavePrerequisites() == null) return;

    setSaveAsOpen(true);
  };

  const onSaveAsSubmit = (reportName: string) => {
    setSaveAsLoading(true);

    const payload = buildReportSavePayload({
      reportTitle: reportName,
      reportMode: "SNAPSHOT",
    });

    dispatch(
      saveReportDataSagaAction({
        payload,
        onSuccess: (resp) => {
          setSaveAsLoading(false);
          setSaveAsOpen(false);

          if (resp?.report_id && categoryKey) {
            navigate(
              `/hid-admin/dashboard/reports-dashboard/category/${categoryKey}/report/${resp.report_id}`,
              { replace: true }
            );
          }

          setPopup({
            open: true,
            message: "Report copy saved successfully.",
            type: "success",
          });
        },
        onError: (errorMessage: string) => {
          setSaveAsLoading(false);
          setPopup({
            open: true,
            message: errorMessage || "Unable to save report copy.",
            type: "error",
          });
        },
      })
    );
  };

  const saveAsDefaultName = `${(headerData?.title || "Untitled").trim()} COPY`;


  const onExportReport = () => {
    const report_title = (headerData?.title || "Untitled").trim();
    const category_id = Number(headerData?.categoryKey ?? categoryKey);
    const userId = loggedUserDetail.ID;
    const reportId = Number(headerData?.id);

    const selectColumns = appliedColumns?.length ? appliedColumns : [];
    const filters = buildFiltersPayload(appliedFilters);

    const dateField: "month" | "year" =
      chartSettings.year === "year" ? "year" : "month";

    const chart = chartSettings.applied ? {
      type: chartSettings.chartType,
      title: chartSettings.title || "",
      xAxis: { field: chartSettings.xAxis || "" },
      yAxis: { aggregation: "count" as const },
      dateField,
      dateRange: {
        from: chartSettings.dateRange.from || "",
        to: chartSettings.dateRange.to || "",
      },
    } : {
      type: "",
      title: "",
      xAxis: { field: "" },
      yAxis: { aggregation: "count" as const },
      dateField: "month" as const,
      dateRange: { from: "", to: "" }
    };

    const payload = {
      report_title,
      user: loggedUserDetail.ID,
      category_id,
      definition: {
        selectColumns,
        filters,
        chart,
      }
    };
    dispatch(
      exportReportSagaAction({
        reportId,
        userId,
      })
    );
  };

  return (
    <>
      <ErrorPopup
        open={popup.open}
        message={popup.message}
        type={popup.type}
        onClose={closePopup}
      />
      <SaveAsReportModal
        open={saveAsOpen}
        initialName={saveAsDefaultName}
        loading={saveAsLoading}
        onClose={() => {
          if (!saveAsLoading) setSaveAsOpen(false);
        }}
        onSubmit={onSaveAsSubmit}
      />
      <div className="rd-report-detail" style={{ display: "flex", flexDirection: "column" }}>
        <ReportsDetailHeader
          data={headerData}
          onSave={onSaveReport}
          onSaveAs={onOpenSaveAs}
          onCancel={() => navigate(-1)}
          onExport={onExportReport}
          exportDisabled={exportDisabled}
          showExport={canEditReport || isSnapshotReport}
          showSaveActions={canSaveAndApply}
          showCancel={!isSnapshotReport}
          saveLoading={saveAsLoading}
        />

        <div
          className={`rd-report-body${
            isSnapshotReport ? " rd-report-body--full-width" : ""
          }`}
          style={{ display: "flex", flex: 1, marginTop: 16 }}
        >
          <div
            className={`rd-report-left${
              isSnapshotReport ? " rd-report-left--full-width" : ""
            }`}
            style={{ flex: isSnapshotReport ? 1 : 3 }}
          >
            <ReportCategoryAccess
              categoriesList={categoriesList.map(c => c.title)}
              initialCategory={selectedCategory}
              accessInfo={accessInfo}
              onCategoryChange={handleCategoryChange}
              onEditAccess={() => console.log("Edit Access clicked")}
            />
            {(chartSettings.applied || showTable) ? (
              <>
                {chartSettings.applied && (
                  <div className="rd-report-graph" style={{ marginBottom: 24 }}>
                    <h3>{chartSettings.title || "Chart"}</h3>
                    <Graph
                      data={chartData}
                      chartType={chart?.type ?? "line"}
                      xAxisLabel={chartData.xAxisLabel}
                      yAxisLabel={chartData.yAxisLabel}
                      truncateXAxisLabels={false}
                    />
                  </div>
                )}

                {showTable && (
                  <div className="rd-report-table" style={{ marginTop: 0 }}>
                    <DataTable
                      rows={showTableData}
                      columns={tableColumns}
                      columnSorts={columnSorts}
                      setColumnSorts={setColumnSorts}
                    />
                  </div>

                )}
              </>
            ) : (
              <ReportEmpty
                title=""
                message="No records returned in preview. Try running the report or editing report filters. "
                showButton={false}
              />
            )}
          </div>

          {!isSnapshotReport ? (
            <div className="rd-report-right" style={{ flex: 1 }}>
              <FilterPanel
                onApplyOutline={handleApplyOutline}
                onResetOutline={handleResetOutline}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                chartSettings={chartSettings}
                setChartSettings={setChartSettings}
                hasTableData={hasTableData}
                groupColumns={groupColumns}
                setGroupColumns={setGroupColumns}
                groupRows={groupRows}
                setGroupRows={setGroupRows}
                tableColumns={tableColumns}
                columnLabelMap={columnLabelMap}
                readOnly={!canSaveAndApply}
                disableInputs={!canSaveAndApply}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ReportsDetailPage;